/**
 * THE ONE MODEL behind every Super Admin payment-types screen.
 *
 * The terminal's Payment Types tab and the order's device card both edit a
 * register through these functions, so the two cannot drift. Approved mockup:
 * docs/agenticpos-rubytill/mockups/payment-types-three-layer.html (tab 1).
 *
 *   Super Admin GRANTS (location gateway row + register override)
 *   Merchant CHOOSES inside the grant (register flags + location values)
 *   The till OBEYS — and the server pushes it, never the browser.
 *
 * A save sends ONLY what changed. The ext3 handler treats every absent field
 * as KEEP, so a field nobody touched is never rewritten — "follows location"
 * stays unset, an emergency override stays on, and the merchant's own
 * enabled list is never reset to the assigned one.
 */
import { readSignatureRequired } from '../signatureRequired';

/** Mirror of APOS_CREDIT_TYPES — one of these per register (server mutex). */
export const CREDIT_TYPES = ['credit_external', 'credit_nmi_cloud', 'credit_nmi_direct', 'credit_dejavoo', 'credit_dejavoo_cloud', 'credit_dejavoo_rtpay', 'credit_magtek', 'credit_sunbay'] as const;
/** Card tenders driven by a reader bound to the register — "Credit". */
export const DEVICE_CREDIT_TYPES = ['credit_nmi_cloud', 'credit_nmi_direct', 'credit_dejavoo', 'credit_dejavoo_cloud', 'credit_dejavoo_rtpay', 'credit_magtek', 'credit_sunbay'] as const;
/** A card machine the shop already has — "Card — external terminal". Two ids, one tender. */
export const EXTERNAL_CARD_ALIASES = ['credit_external', 'external_card'] as const;

/** Device integration class → the credit tender it drives. Explicit, no fall-through. */
export const DEVICE_CLASS_TO_METHOD: Record<string, string> = {
  nmi_direct_vp3350: 'credit_nmi_direct',
  dejavoo_spin_usb: 'credit_dejavoo',
  dejavoo_spin_cloud: 'credit_dejavoo_cloud',
  /* RubyTill Pay — our app on the P-series terminal, reached through our relay. */
  dejavoo_rubytill_pay: 'credit_dejavoo_rtpay',
  cloud: 'credit_nmi_cloud',
};

/**
 * THE CREDIT TENDER A READER DRIVES — by its model's shared-types definition
 * first (caller passes it in), then by its enrolled class. Unknown → null, and
 * the caller must say so: guessing `credit_nmi_cloud` bound a USB or direct
 * reader as a cloud lane that could never take a card.
 */
export function creditMethodForDevice(d: { deviceClass?: string } | null | undefined, byModel?: string | null): string | null {
  if (byModel) return byModel;
  const cls = String(d?.deviceClass || '');
  return DEVICE_CLASS_TO_METHOD[cls] || null;
}

/** Why a reader cannot be assigned as Credit — the one message every screen shows. */
export const unknownReaderMessage = (d: { deviceClass?: string; deviceName?: string } | null | undefined) =>
  `${d?.deviceName || 'This reader'} is enrolled as "${d?.deviceClass || 'unknown type'}", which has no card tender — re-register it with the right reader type before assigning Credit.`;

/**
 * A STATUS THAT MEANS THE REGISTER ROW EXISTS. `pos-terminals-list` serves the
 * PROVISION row's status before a terminal is claimed and the REGISTER row's
 * status afterwards, so any of these is evidence the claim happened. Mirrors
 * `APosRegisterStatusSchema` plus `activated` (the provision row's word).
 */
export const REGISTER_EXISTS_STATUSES: readonly string[] = ['active', 'in_session', 'maintenance', 'inactive', 'activated'];

/** True when the terminal has (or may have) a register — unknown status counts as yes. */
export const registerMayExist = (t: { status?: string | null; registerStatus?: string | null }) =>
  !!t.registerStatus || !t.status || REGISTER_EXISTS_STATUSES.includes(t.status);

/** How the reader reaches the till — the only reader fact a row may name. */
export const TRANSPORT_FOR_CLASS: Record<string, string> = {
  dejavoo_spin_usb: 'USB',
  dejavoo_spin_cloud: 'Cloud',
  /* Our own product, not the vendor — allowed on a row. */
  dejavoo_rubytill_pay: 'RubyTill Pay',
  nmi_direct_vp3350: 'Direct',
  cloud: 'Cloud',
};

const isCredit = (m: string) => (CREDIT_TYPES as readonly string[]).includes(m) || m === 'external_card';
const isDeviceCredit = (m: string) => (DEVICE_CREDIT_TYPES as readonly string[]).includes(m);
const isExternal = (m: string) => (EXTERNAL_CARD_ALIASES as readonly string[]).includes(m);

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

/** What the register STORES — `null` is "unset", which is a real value. */
export interface RegisterState {
  assigned: string[];
  enabled: string[];
  overrideActive: boolean;
  signatureRequired: Record<string, boolean>;
  /** null = follows the location (`tenantSurchargeConfig.apply`). */
  surchargeApply: boolean | null;
  /** null = follows surcharge. */
  debitEnabled: boolean | null;
  /** The reader bound to this register (EMV pool id), if any. */
  emvDeviceId: string | null;
}

/** Super Admin's grants on the location's gateway row. */
export interface GrantState {
  tipsEnabled: boolean;
  cashTipsEnabled: boolean;
  surchargeEnabled: boolean;
}

export const emptyRegister = (): RegisterState => ({
  assigned: [], enabled: [], overrideActive: false, signatureRequired: {},
  surchargeApply: null, debitEnabled: null, emvDeviceId: null,
});

/** True when the GET carries the stored flags itself — no raw-row read needed. */
export const hasStoredFlags = (pt: any) =>
  !!pt && pt.surchargeApplyStored !== undefined && pt.debitEnabledStored !== undefined;

/**
 * READ A REGISTER from the ext3 GET plus, when needed, its raw row.
 *
 * `surchargeApply` / `debitEnabled` on the GET are EFFECTIVE values — they
 * cannot say "unset". The GET's `surchargeApplyStored` / `debitEnabledStored`
 * (boolean | null) can, and win when present. An older GET without them falls
 * back to the raw register row (ext2 `listRegisters`); with neither, the
 * effective value is taken as stored and no "follows" pill is drawn — never a
 * confident claim the data cannot back.
 */
export function registerFromResponses(pt: any, raw: any | null, emvDeviceId: string | null): RegisterState {
  const rawPt = raw ? (raw.paymentTypes || raw.paymentConfig || {}) : null;
  const pick = (k: 'surchargeApply' | 'debitEnabled'): boolean | null => {
    const stored = pt?.[`${k}Stored`];
    if (stored !== undefined) return typeof stored === 'boolean' ? stored : null;
    if (rawPt) return typeof rawPt[k] === 'boolean' ? rawPt[k] : null;
    return typeof pt?.[k] === 'boolean' ? pt[k] : null;
  };
  const sigSrc = pt?.signatureRequired && typeof pt.signatureRequired === 'object' && Object.keys(pt.signatureRequired).length
    ? pt.signatureRequired
    : (rawPt?.signatureRequired || pt?.signatureRequired || {});
  const sig: Record<string, boolean> = {};
  for (const [k, v] of Object.entries(sigSrc || {})) if (typeof v === 'boolean') sig[k] = v;
  return {
    assigned: Array.isArray(pt?.assigned) ? [...pt.assigned] : [],
    enabled: Array.isArray(pt?.enabled) ? [...pt.enabled] : [],
    overrideActive: pt?.overrideActive === true,
    signatureRequired: sig,
    surchargeApply: pick('surchargeApply'),
    debitEnabled: pick('debitEnabled'),
    emvDeviceId,
  };
}

export const grantsFromGateway = (gw: any): GrantState => ({
  tipsEnabled: !!gw?.tipsEnabled,
  cashTipsEnabled: !!gw?.cashTipsEnabled,
  surchargeEnabled: !!gw?.surchargeEnabled,
});

// ---------------------------------------------------------------------------
// Derived — mirrors super-admin-ext3 lib/register-payment-flags.ts
// ---------------------------------------------------------------------------

export function activeAgreementOf(gw: any): any | null {
  const list: any[] = Array.isArray(gw?.surchargeAgreements) ? gw.surchargeAgreements : [];
  return list.find((a) => a.id === gw?.activeSurchargeAgreementId) || null;
}

/** Approved = the grant is on AND a signed agreement backs it. */
export const isSurchargeApproved = (gw: any, grants: GrantState) =>
  grants.surchargeEnabled && !!activeAgreementOf(gw);

export const locationApplies = (gw: any) => gw?.tenantSurchargeConfig?.apply !== false;

export function effectiveSurcharge(r: RegisterState, gw: any, grants: GrantState): boolean {
  if (!isSurchargeApproved(gw, grants)) return false;
  return r.surchargeApply === null ? locationApplies(gw) : r.surchargeApply;
}

export function effectiveDebit(r: RegisterState, gw: any, grants: GrantState): boolean {
  const s = effectiveSurcharge(r, gw, grants);
  if (s) return true;
  if (r.debitEnabled !== null) return r.debitEnabled;
  return r.surchargeApply !== null ? r.surchargeApply : s;
}

export const creditTenderOf = (r: RegisterState) => r.assigned.find(isDeviceCredit) || null;
export const externalTenderOf = (r: RegisterState) => r.assigned.find(isExternal) || null;

/** On at the till: assigned, and either the merchant has it on or override forces it. */
export function tenderLive(r: RegisterState, ids: readonly string[]): boolean {
  const as = r.assigned.some((m) => ids.includes(m));
  if (!as) return false;
  return r.overrideActive || r.enabled.some((m) => ids.includes(m));
}

/** Assigned by Super Admin but switched off by the merchant (override off). */
export function merchantTurnedOff(r: RegisterState, ids: readonly string[]): boolean {
  return r.assigned.some((m) => ids.includes(m)) && !r.overrideActive && !r.enabled.some((m) => ids.includes(m));
}

/** The signature key under the Credit row — the card tender's own id. */
export const SIG_DEBIT = 'debit';

// ---------------------------------------------------------------------------
// Edits (pure — return a new state)
// ---------------------------------------------------------------------------

/** Assign (and enable) a tender; credit tenders replace each other. */
export function assignTender(r: RegisterState, id: string, emvDeviceId?: string | null): RegisterState {
  const drop = (list: string[]) => list.filter((m) => !(isCredit(id) && isCredit(m)) && !(isExternal(id) && isExternal(m)) && m !== id);
  const canonical = isExternal(id) ? 'credit_external' : id;
  return {
    ...r,
    assigned: [...drop(r.assigned), canonical],
    enabled: [...drop(r.enabled), canonical],
    emvDeviceId: isDeviceCredit(id) ? (emvDeviceId ?? r.emvDeviceId) : (isCredit(id) ? null : r.emvDeviceId),
  };
}

/** Unassign a tender (both ids for the external card machine). */
export function unassignTender(r: RegisterState, id: string): RegisterState {
  const ids = isExternal(id) ? (EXTERNAL_CARD_ALIASES as readonly string[]) : [id];
  return {
    ...r,
    assigned: r.assigned.filter((m) => !ids.includes(m)),
    enabled: r.enabled.filter((m) => !ids.includes(m)),
    emvDeviceId: isDeviceCredit(id) ? null : r.emvDeviceId,
  };
}

export function toggleSig(r: RegisterState, key: string): RegisterState {
  return { ...r, signatureRequired: { ...r.signatureRequired, [key]: !readSignatureRequired(r.signatureRequired, key) } };
}

/** Surcharge ⇒ debit: turning surcharge on turns debit on (and the UI locks it). */
export function setSurchargeApply(r: RegisterState, on: boolean | null): RegisterState {
  return { ...r, surchargeApply: on, ...(on === true ? { debitEnabled: true } : {}) };
}

// ---------------------------------------------------------------------------
// Diff → the request bodies
// ---------------------------------------------------------------------------

const sameSet = (a: string[], b: string[]) => {
  const x = new Set(a), y = new Set(b);
  return x.size === y.size && [...x].every((v) => y.has(v));
};

/** Signature keys whose effective value differs. */
export function changedSigKeys(base: RegisterState, draft: RegisterState): string[] {
  const keys = new Set([...Object.keys(base.signatureRequired), ...Object.keys(draft.signatureRequired)]);
  return [...keys].filter((k) => readSignatureRequired(base.signatureRequired, k) !== readSignatureRequired(draft.signatureRequired, k));
}

/**
 * THE REGISTER PUT BODY — changed fields only, or null when nothing changed.
 * `emvDeviceId` goes with a newly assigned reader-backed credit tender so the
 * server binds it in the same write.
 */
export function buildRegisterPatch(base: RegisterState, draft: RegisterState): Record<string, unknown> | null {
  const out: Record<string, unknown> = {};
  if (!sameSet(base.assigned, draft.assigned)) out.assigned = draft.assigned;
  if (!sameSet(base.enabled, draft.enabled)) out.enabled = draft.enabled;
  if (base.overrideActive !== draft.overrideActive) out.overrideActive = draft.overrideActive;
  if ((base.emvDeviceId || null) !== (draft.emvDeviceId || null) && creditTenderOf(draft)) out.emvDeviceId = draft.emvDeviceId;
  const sigKeys = changedSigKeys(base, draft);
  if (sigKeys.length) {
    out.signatureRequired = Object.fromEntries(sigKeys.map((k) => [k, readSignatureRequired(draft.signatureRequired, k)]));
  }
  if (base.surchargeApply !== draft.surchargeApply) out.surchargeApply = draft.surchargeApply;
  // null = back to "follows surcharge" (the server stores it as unset).
  if (base.debitEnabled !== draft.debitEnabled) out.debitEnabled = draft.debitEnabled;
  return Object.keys(out).length ? out : null;
}

export function buildGrantPatch(base: GrantState, draft: GrantState): Record<string, boolean> | null {
  const out: Record<string, boolean> = {};
  (Object.keys(draft) as (keyof GrantState)[]).forEach((k) => { if (base[k] !== draft[k]) out[k] = draft[k]; });
  return Object.keys(out).length ? out : null;
}

/** How many separate things the save bar should count. */
export function countChanges(regPatch: Record<string, unknown> | null, grantPatch: Record<string, boolean> | null): number {
  let n = 0;
  if (regPatch) {
    for (const [k, v] of Object.entries(regPatch)) {
      if (k === 'emvDeviceId') continue; // rides with the credit assignment
      n += k === 'signatureRequired' ? Object.keys(v as object).length : 1;
    }
  }
  if (grantPatch) n += Object.keys(grantPatch).length;
  return n;
}

export interface BulkFields {
  cardEnabled?: boolean;
  signatureRequired?: { cash?: boolean; credit?: boolean; debit?: boolean };
  surchargeApply?: boolean | null;
  debitEnabled?: boolean | null;
}

/**
 * WHAT A BROADCAST COPIES — the register-level choices that mean the same on
 * any till. Assignment, override and reader binding are per-till facts and
 * never broadcast. The Credit signature is keyed by this till's card tender
 * and sent as `credit`, so the server maps it onto each till's own tender.
 */
export function buildBulkFields(base: RegisterState, draft: RegisterState): BulkFields | null {
  const out: BulkFields = {};
  const credit = creditTenderOf(draft);
  if (credit && credit === creditTenderOf(base)) {
    const was = base.enabled.includes(credit), now = draft.enabled.includes(credit);
    if (was !== now) out.cardEnabled = now;
  }
  const sig: BulkFields['signatureRequired'] = {};
  for (const k of changedSigKeys(base, draft)) {
    const v = readSignatureRequired(draft.signatureRequired, k);
    if (k === 'cash') sig.cash = v;
    else if (k === SIG_DEBIT) sig.debit = v;
    else if (k === credit) sig.credit = v;
  }
  if (Object.keys(sig).length) out.signatureRequired = sig;
  if (base.surchargeApply !== draft.surchargeApply) out.surchargeApply = draft.surchargeApply;
  if (base.debitEnabled !== draft.debitEnabled) out.debitEnabled = draft.debitEnabled;
  return Object.keys(out).length ? out : null;
}

const FIELD_LABEL: Record<string, string> = {
  cardEnabled: 'Credit', surchargeApply: 'Surcharge', debitEnabled: 'Debit',
  'signatureRequired.cash': 'Cash signature', 'signatureRequired.credit': 'Credit signature',
  'signatureRequired.debit': 'Debit signature',
};
const fmtVal = (v: unknown, field: string) => {
  if (v === null || v === undefined) return field === 'surchargeApply' ? 'follows location' : field === 'debitEnabled' ? 'follows surcharge' : 'unset';
  if (v === true) return 'on';
  if (v === false) return 'off';
  return String(v);
};
/** "Surcharge follows location → off" */
export const describeChange = (c: { field: string; from: unknown; to: unknown }) =>
  `${FIELD_LABEL[c.field] || c.field} ${fmtVal(c.from, c.field)} → ${fmtVal(c.to, c.field)}`;

export const BULK_FIELD_LABEL = (f: BulkFields): string[] => {
  const out: string[] = [];
  if (f.cardEnabled !== undefined) out.push('Credit on/off');
  if (f.signatureRequired) out.push('Signatures');
  if (f.surchargeApply !== undefined) out.push('Surcharge');
  if (f.debitEnabled !== undefined) out.push('Debit');
  return out;
};
